import React from "react";
import {Link} from "react-router-dom";
import {RoutePaths} from "../../../../Constants/RoutePaths";
import IArticleCategoryListDTO from "../../../../Models/DTOs/IArticleCategoryListDTO";
import IckonicAds320x250 from "../../../../Models/StaticObjects/IckonicAds320x250";
import ArticleDTO from "../../../../Models/DTOs/ArticleDTO";
import ArticleFeedCard from "./ArticleFeedCard";

interface ArticlesGridProps {
    articleGroups?: IArticleCategoryListDTO[];
    mode?: "editorial" | "compact";
    featuredArticle?: ArticleDTO | null;
    featuredIsStaffPick?: boolean;
    disableLoadMore: boolean;
    loadMoreUpdate?: () => void;
    isLoading: boolean;
    doesAuthTokenExist: boolean;
    emptyMessage?: string;
}

const ArticlesGrid: React.FC<ArticlesGridProps> = ({
    articleGroups = [], mode = "editorial", featuredArticle, featuredIsStaffPick = false, disableLoadMore,
    loadMoreUpdate, isLoading, doesAuthTokenExist, emptyMessage = "No articles to show yet. Check back soon."
}) => {
    const groups = articleGroups.filter(group => group.Articles.length > 0);
    const hasArticles = !!featuredArticle || groups.length > 0;
    const eligibleAds = IckonicAds320x250
        .filter(ad => !(ad.LoggedOutOnly && doesAuthTokenExist))
        .slice().sort((a, b) => a.Priority - b.Priority);
    const ad = eligibleAds[0];
    // Only the main feed gets a promotion, after five regular stories. Topic
    // previews and shorter feeds remain uninterrupted, with no empty ad slot.
    const promotionGroupIndex = groups.findIndex(group =>
        (group.Id === -1 || !group.Title?.trim()) && group.Articles.length >= 5
    );

    if (!hasArticles) {
        return <div className={`layout--articles-grid article-feed article-feed--${mode}`}>
            {isLoading ? <div className="article-feed__loading" role="status" aria-label="Loading articles">
                <span className="article-feed__sr-only">Loading articles...</span>
                <div className="article-feed__skeleton article-feed__skeleton--hero" aria-hidden="true" />
                <div className="article-feed__skeleton-grid" aria-hidden="true">
                    {[0, 1, 2].map(index => <div className="article-feed__skeleton" key={index} />)}
                </div>
            </div> : <p className="article-feed__empty" role="status">{emptyMessage}</p>}
        </div>;
    }

    return <div className={`layout--articles-grid article-feed article-feed--${mode}`}>
        {featuredArticle && <div className="article-feed__lead">
            <ArticleFeedCard article={featuredArticle} variant="hero" showStaffPick={featuredIsStaffPick} />
        </div>}
        {groups.map((group, groupIndex) => {
            const showHeading = !!group.Title?.trim();
            const isPrimary = group.Id === -1 || !showHeading;
            const groupHeadingId = `article-feed-group-${group.Id}-${groupIndex}`;
            return <section className="article-feed__section" key={`${group.Id}-${groupIndex}`}
                aria-labelledby={showHeading ? groupHeadingId : undefined}>
                {showHeading && <div className="article-feed__section-heading">
                    <h2 id={groupHeadingId}>{group.Title}</h2>
                    {group.Id !== -1 && <Link to={RoutePaths.ArticleCategory(group.Id)} aria-label={`View all ${group.Title} articles`}>
                        View all <span aria-hidden="true">&rarr;</span>
                    </Link>}
                </div>}
                <div className={`article-feed__stories article-feed__stories--cards${isPrimary ? " article-feed__stories--primary" : ""}`}>
                    {group.Articles.map((article, index) => <React.Fragment key={article.id}>
                        <ArticleFeedCard article={article} headingLevel={showHeading ? 3 : 2}
                            showStaffPick={article.id === group.PickArticleId} />
                        {groupIndex === promotionGroupIndex && index === 4 && ad &&
                            <aside className="article-feed__promotion" aria-label="Advertisement">
                                <span className="article-feed__promotion-label">Advertisement</span>
                                <a href={ad.Link} target={ad.Target} rel={ad.Target === "_blank" ? "noopener noreferrer" : undefined}>
                                    <img src={ad.Image} alt={ad.LoggedOutOnly ? "Explore Ickonic membership" : "Explore the Ickonic shop"}
                                        width={320} height={250} loading="lazy" decoding="async" />
                                </a>
                            </aside>}
                    </React.Fragment>)}
                </div>
            </section>;
        })}
        {!disableLoadMore && loadMoreUpdate && <div className="article-feed__load-more">
            <button type="button" onClick={loadMoreUpdate} disabled={isLoading}>
                {isLoading ? "Loading articles..." : "Load more articles"}
            </button>
        </div>}
        <div className="article-feed__sr-only" role="status" aria-live="polite">{isLoading ? "Loading more articles..." : ""}</div>
    </div>;
};

export default ArticlesGrid;
